import { supabase } from "@/integrations/supabase/client";
import { detectDevice } from "@/lib/device";

const SESSION_KEY = "feo_session_id";
const SESSION_TS_KEY = "feo_session_ts";
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 min idle

// Bots/crawlers/health checks que não devem entrar no dashboard como sessão real.
const BOT_UA_REGEX =
  /bot|crawler|spider|headless|bytespider|facebookexternalhit|meta-externalads|sebot|google-read-aloud|adsbot|lighthouse|pingdom|uptime|monitor/i;

function isBot(): boolean {
  if (typeof navigator === "undefined") return true;
  const ua = navigator.userAgent || "";
  if (!ua) return true;
  if (BOT_UA_REGEX.test(ua)) return true;
  // navigator.webdriver é true em Playwright/Selenium/headless automatizado
  if ((navigator as Navigator & { webdriver?: boolean }).webdriver) return true;
  return false;
}

function uuid() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "s-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function getSessionId(): string {
  if (typeof window === "undefined") return "ssr";
  const now = Date.now();
  const lastTs = Number(sessionStorage.getItem(SESSION_TS_KEY) || "0");
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id || now - lastTs > SESSION_TTL_MS) {
    id = uuid();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  sessionStorage.setItem(SESSION_TS_KEY, String(now));
  return id;
}

function readUtmsFromSearch(search: string): Record<string, string | null> {
  const p = new URLSearchParams(search);
  const get = (k: string) => p.get(k);
  return {
    utm_source: get("utm_source"),
    utm_medium: get("utm_medium"),
    utm_campaign: get("utm_campaign"),
    utm_content: get("utm_content"),
    utm_term: get("utm_term"),
  };
}

const UTM_STORAGE_KEY = "feo_utms";

function persistUtmsIfPresent(utms: Record<string, string | null>) {
  if (typeof window === "undefined") return;
  const hasAny = Object.values(utms).some(Boolean);
  if (!hasAny) return;
  try {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utms));
  } catch {
    /* ignore */
  }
}

export function getStoredUtms(): Record<string, string | null> {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export async function trackPageView(path: string) {
  if (typeof window === "undefined") return;
  // never track admin
  if (path.startsWith("/admin")) return;
  if (isBot()) return;

  const urlUtms = readUtmsFromSearch(window.location.search);
  persistUtmsIfPresent(urlUtms);
  const utms = { ...getStoredUtms(), ...Object.fromEntries(Object.entries(urlUtms).filter(([, v]) => v)) };

  try {
    await supabase.from("page_views").insert({
      path,
      session_id: getSessionId(),
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      device: detectDevice(),
      utm_source: utms.utm_source ?? null,
      utm_medium: utms.utm_medium ?? null,
      utm_campaign: utms.utm_campaign ?? null,
      utm_content: utms.utm_content ?? null,
      utm_term: utms.utm_term ?? null,
    });
  } catch {
    /* fire-and-forget */
  }
}

export async function trackButtonClick(opts: {
  buttonId?: string;
  buttonLabel?: string;
  destination?: string;
  path?: string;
}) {
  if (typeof window === "undefined") return;
  const path = opts.path ?? window.location.pathname;
  if (path.startsWith("/admin")) return;
  if (isBot()) return;
  try {
    await supabase.from("button_clicks").insert({
      path,
      session_id: getSessionId(),
      button_id: opts.buttonId ?? null,
      button_label: opts.buttonLabel ?? null,
      destination: opts.destination ?? null,
      device: detectDevice(),
    });
  } catch {
    /* ignore */
  }
}
